import { Box, IconButton, MenuItem, TextField, Tooltip, Typography } from '@material-ui/core';
import { useHistory } from 'react-router-dom';
import { Cancel, CheckCircle, Close, Description, Error, HourglassEmpty, ReportProblemOutlined, Sync, WatchLater } from '@material-ui/icons';
import AttachFile from '@material-ui/icons/AttachFile';
import { useGenericStyles } from 'common/genericStyles';
import {
  CompraVendaStatus,
  OperacaoCommonStatus,
  OperacaoCompraVendaResponse,
  CompraVendaResumoResponse,
  ComprasVendasFilter,
  GETComprasVendasFilterParams,
  CompraVendaDocumento,
  GrupoAcesso,
} from 'common/genericTypes';
import { Alert } from 'components/alert';
import LegendaStatus from 'pages/operacoes/gerenciar/legendaStatus';
import { contraparteNaoPodeLiquidarRetorno } from 'pages/operacoes/gerenciar/liquidacaoRetornoCompromissada';
import { GenericDrawer } from 'components/genericDrawer';
import { Button, MdSpace, SmSpace } from 'm2m.components';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { addCpfCnpjMask } from 'utils/mask';
import { formatEightDecimalsCurrency } from 'utils/number';
import { formatInstrumentoCurrency } from 'utils/instrumentoFinanceiroUtils';
import {
  DataGridPro,
  getGridDateOperators,
  getGridSingleSelectOperators,
  getGridStringOperators,
  GridCellParams,
  GridFilterModel,
  GridSelectionModel,
  GridSortModel,
  GridValueGetterParams,
} from '@mui/x-data-grid-pro';
import MoreOptionsMenu from 'components/moreOptionsMenu';
import { GestaoComprovantesDrawer } from 'pages/operacoes/compraVenda/gestaoComprovantesDrawer';
import { AprovacaoEmLoteModal } from 'pages/operacoes/gerenciar/aprovacaoEmLoteModal';
import ToolbarExportacaoMovimentacoes from 'utils/dataGrid/toolbarExportacaoMovimentacoes';
import { colunasRelatorioMovimentacao } from 'pages/operacoes/gerenciar/colunasRelatorioMovimentacao';
import { colunaDocumentoCompraVenda } from 'pages/operacoes/gerenciar/colunaDocumentoMovimentacao';
import { componentsPropsValues, rowsPerPageOptionsValues } from 'utils/dataGrid/dataGridUtils';
import { localizedTextsMap } from 'utils/dataGridUtils';
import { formatDate } from 'utils/date';
import { operacaoStatusDescriptions } from 'common/genericDescriptions';

enum DrawerAction {
  ACEITAR,
  REJEITAR,
  CANCELAR,
  CONFIRMAR_LIQUIDACAO,
  APROVACAO_ADM_FIDUCIARIO,
}

export const safeGet = <T,>(obj: any, path: string, defaultValue: T): T => {
  return path.split('.').reduce((acc, key) => {
    try {
      return acc && acc[key] !== undefined ? acc[key] : defaultValue;
    } catch (e) {
      return defaultValue;
    }
  }, obj);
};

const getStatusIcon = (status: string, classes: any) => {
  switch (status) {
    case OperacaoCommonStatus.EmAnalise:
      return <Error fontSize="small" className={classes.waitingIcon} />;
    case CompraVendaStatus.Cancelada:
    case OperacaoCommonStatus.Reprovada:
      return <Cancel fontSize="small" className={classes.errorIcon} />;
    case OperacaoCommonStatus.Concluida:
      return <CheckCircle fontSize="small" className={classes.successIcon} />;
    case CompraVendaStatus.AguardandoLiquidacao:
      return <WatchLater fontSize="small" className={classes.warningIcon} />;
    case CompraVendaStatus.AguardandoAprovacaoADMFiduciario:
      return <ReportProblemOutlined fontSize="small" className={classes.waitingIcon} />;
    case CompraVendaStatus.AceiteOrigemRealizado:
    case CompraVendaStatus.AceiteDestinoRealizado:
      return <HourglassEmpty fontSize="small" className={classes.warningIcon} />;
    case CompraVendaStatus.LiquidacaoEmProcessamento:
      return <Sync fontSize="small" className={classes.warningIcon} />;
    default:
      return <></>;
  }
};

const ComprasVendas: React.FC = () => {
  const classes = useGenericStyles();
  const history = useHistory();

  const [operacoes, setOperacoes] = useState<Array<CompraVendaResumoResponse>>([]);
  const [page, setPage] = useState<number>(0);
  const [pageCount, setPageCount] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(20);
  const [linkOperator, setLinkOperator] = useState<any>('AND');
  const [comprasVendasFilter, setComprasVendasFilter] = useState<Array<ComprasVendasFilter>>([]);

  const [operacao, setOperacao] = useState<OperacaoCompraVendaResponse>();
  const [loadingDrawer, setLoadingDrawer] = useState<boolean>(false);
  const [openAprovarRejeitarDrawer, setOpenAprovarRejeitarDrawer] = useState<boolean>(false);
  const [openAprovarRejeitarADMFiduciarioDrawer, setOpenAprovarRejeitarADMFiduciarioDrawer] = useState<boolean>(false);
  const [openConfirmarLiquidacaoDrawer, setOpenConfirmarLiquidacaoDrawer] = useState<boolean>(false);
  const [instrumentoInadimplido, setInstrumentoInadimplido] = useState<boolean>(false);
  const [drawerAction, setDrawerAction] = useState<DrawerAction>(DrawerAction.ACEITAR);
  const [showStatusToast, setShowStatusToast] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [justificativa, setJustificativa] = useState<string>('');

  const [openGestaoComprovantesDrawer, setOpenGestaoComprovantesDrawer] = useState<boolean>(false);
  const [comprovantesOperacaoId, setComprovantesOperacaoId] = useState<string | undefined>(undefined);
  const [comprovantesCodigoIF, setComprovantesCodigoIF] = useState<string | undefined>(undefined);
  const [compraVendaDocumentos, setCompraVendaDocumentos] = useState<Array<CompraVendaDocumento>>([]);

  const [selectionModel, setSelectionModel] = useState<GridSelectionModel>([]);
  const [openAprovacaoEmLoteModal, setOpenAprovacaoEmLoteModal] = useState<boolean>(false);
  const dispatch = useDispatch();

  const postComprasVendasPaginated = useSelector((state: RootState) => state.operacaoApi.postComprasVendasPaginated);
  const postComprasVendasRelatorioCompleto = useSelector(
    (state: RootState) => state.operacaoApi.postComprasVendasRelatorioCompleto
  );
  const getOperacaoCompraVendaDetalhes = useSelector(
    (state: RootState) => state.operacaoApi.getOperacaoCompraVendaDetalhes
  );
  const postAceitarCompraVenda = useSelector((state: RootState) => state.operacaoApi.postAceitarCompraVenda);
  const postConfirmarLiquidacaoCompraVenda = useSelector(
    (state: RootState) => state.operacaoApi.postConfirmarLiquidacaoCompraVenda
  );
  const participanteId = useSelector((state: RootState) => state.auth.usuario.participante.id);
  const participanteNome = useSelector((state: RootState) => state.auth.usuario.participante.razaoSocial);
  const isUsuarioOperacoes = useSelector(
    (state: RootState) => state.auth.usuario.grupoAcesso === GrupoAcesso.Operacoes);
  const postRejeitarCompraVenda = useSelector((state: RootState) => state.operacaoApi.postRejeitarCompraVenda);
  const postCancelarCompraVenda = useSelector((state: RootState) => state.operacaoApi.postCancelarCompraVenda);
  const getCompraVendaDocumentos = useSelector((state: RootState) => state.operacaoApi.getCompraVendaDocumentos);

  const handleOpenDrawer = async (action: DrawerAction, operacaoId: string) => {
    try {
      setLoadingDrawer(true);
      setDrawerAction(action);
      setShowStatusToast(false);

      const detalhes = await getOperacaoCompraVendaDetalhes(dispatch, operacaoId);
      setOperacao(detalhes);
      setInstrumentoInadimplido(false);

      action === DrawerAction.CONFIRMAR_LIQUIDACAO
        ? setOpenConfirmarLiquidacaoDrawer(true)
        : action === DrawerAction.APROVACAO_ADM_FIDUCIARIO
        ? setOpenAprovarRejeitarADMFiduciarioDrawer(true)
        : setOpenAprovarRejeitarDrawer(true);
    } catch (error) {
    } finally {
      setLoadingDrawer(false);
    }
  };

  const handleCloseDrawer = () => {
    if (drawerAction === DrawerAction.CONFIRMAR_LIQUIDACAO) {
      setOpenConfirmarLiquidacaoDrawer(false);
    } else if (drawerAction === DrawerAction.APROVACAO_ADM_FIDUCIARIO) {
      setOpenAprovarRejeitarADMFiduciarioDrawer(false);
    } else {
      setOpenAprovarRejeitarDrawer(false);
    }
    setOperacao(undefined);
    setJustificativa('');
  };

  const getOperacoes = useCallback(async () => {
    try {
      const result = await postComprasVendasPaginated(
        dispatch,
        {
          page: page + 1,
          pageSize,
          linkOperator,
          comprasVendasFilter,
        },
        true
      );
      setOperacoes(result.result);
      setPageCount(result.totalCount);
    } catch (error) {
      console.error(error);
    }
  }, [dispatch, postComprasVendasPaginated, page, pageSize, linkOperator, comprasVendasFilter]);

  const carregarTodasAsMovimentacoes = useCallback(
    () =>
      postComprasVendasRelatorioCompleto(
        dispatch,
        {
          linkOperator,
          comprasVendasFilter,
        },
        true
      ),
    [dispatch, postComprasVendasRelatorioCompleto, linkOperator, comprasVendasFilter]
  );

  const statusToastByAction = useCallback(
    (action: DrawerAction) => {
      let divClassName: string = '';
      let resultDescription: string = '';
      let icon: React.ReactNode = null;
      switch (action) {
        case DrawerAction.REJEITAR:
          divClassName = classes.errorToast;
          resultDescription = 'rejeitada';
          icon = <Cancel style={{ marginRight: 10 }} />;
          break;
        case DrawerAction.ACEITAR:
          divClassName = classes.successToast;
          resultDescription = 'aceita';
          icon = <CheckCircle style={{ marginRight: 10 }} />;
          break;
        case DrawerAction.CONFIRMAR_LIQUIDACAO:
          divClassName = classes.successToast;
          resultDescription = 'liquidada';
          icon = <CheckCircle style={{ marginRight: 10 }} />;
          break;
        case DrawerAction.APROVACAO_ADM_FIDUCIARIO:
          divClassName = classes.successToast;
          resultDescription = 'aprovada';
          icon = <CheckCircle style={{ marginRight: 10 }} />;
          break;
      }
      return (
        <>
          <SmSpace />
          <div className={divClassName}>
            {icon}
            <div style={{ display: 'flex', flex: 1 }}>
              <Typography variant="body2">{'Movimentação por'}</Typography>&nbsp;
              <Typography>{`Compra e Venda ${resultDescription}`}</Typography>
              &nbsp;
              <Typography variant="body2">{'com sucesso.'}</Typography>
            </div>
            <IconButton onClick={() => setShowStatusToast(false)}>
              <Close />
            </IconButton>
          </div>
        </>
      );
    },
    [classes]
  );

  const handleAceitar = async () => {
    try {
      if (operacao) {
        setSubmitting(true);
        await postAceitarCompraVenda(dispatch, operacao.id);
        await getOperacoes();
        handleCloseDrawer();
        setShowStatusToast(true);
      }
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirmarLiquidacao = async () => {
    try {
      if (operacao) {
        setSubmitting(true);
        await postConfirmarLiquidacaoCompraVenda(dispatch, operacao.id);
        await getOperacoes();
        handleCloseDrawer();
        setShowStatusToast(true);
      }
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  const handleRejeitar = async () => {
    try {
      if (operacao) {
        setSubmitting(true);
        await postRejeitarCompraVenda(dispatch, operacao.id, justificativa);
        await getOperacoes();
        handleCloseDrawer();
        setShowStatusToast(true);
      }
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelar = async () => {
    try {
      if (operacao) {
        setSubmitting(true);
        await postCancelarCompraVenda(dispatch, operacao.id, justificativa);
        await getOperacoes();
        handleCloseDrawer();
        setShowStatusToast(true);
      }
    } catch (error) {
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenAprovar = useCallback(
    async (item: CompraVendaResumoResponse) => {
      const action =
        item.status === CompraVendaStatus.AguardandoAprovacaoADMFiduciario
          ? DrawerAction.APROVACAO_ADM_FIDUCIARIO
          : DrawerAction.ACEITAR;
      await handleOpenDrawer(action, item.operacaoId);
    },
    [getOperacaoCompraVendaDetalhes, dispatch]
  );

  const handleOpenReprovar = useCallback(
    async (item: CompraVendaResumoResponse) => {
      await handleOpenDrawer(DrawerAction.REJEITAR, item.operacaoId);
    },
    [getOperacaoCompraVendaDetalhes, dispatch]
  );

  const handleOpenCancelar = useCallback(
    async (item: CompraVendaResumoResponse) => {
      await handleOpenDrawer(DrawerAction.CANCELAR, item.operacaoId);
    },
    [getOperacaoCompraVendaDetalhes, dispatch]
  );

  const handleOpenConfirmarLiquidacao = useCallback(
    async (operacaoId: string) => {
      await handleOpenDrawer(DrawerAction.CONFIRMAR_LIQUIDACAO, operacaoId);
    },
    [getOperacaoCompraVendaDetalhes, dispatch]
  );

  const handleOpenGestaoComprovantes = async (item: CompraVendaResumoResponse) => {
    try {
      const docs = await getCompraVendaDocumentos(dispatch, item.operacaoId, true);
      setCompraVendaDocumentos(docs);
    } catch {
      setCompraVendaDocumentos([]);
    }
    setComprovantesOperacaoId(item.operacaoId);
    setComprovantesCodigoIF(item.codigo);
    setOpenGestaoComprovantesDrawer(true);
  };

  const handleCloseGestaoComprovantes = () => {
    setOpenGestaoComprovantesDrawer(false);
    setComprovantesOperacaoId(undefined);
    setComprovantesCodigoIF(undefined);
    setCompraVendaDocumentos([]);
  };

  const handleGestaoComprovantesUploadSuccess = async () => {
    if (comprovantesOperacaoId) {
      try {
        const docs = await getCompraVendaDocumentos(dispatch, comprovantesOperacaoId, true);
        setCompraVendaDocumentos(docs);
      } catch {
        setCompraVendaDocumentos([]);
      }
    }
    await getOperacoes();
  };

  const operacoesSelecionadasEmAnalise = operacoes.filter(
    (op) =>
      selectionModel.includes(op.operacaoId) &&
      (op.status === OperacaoCommonStatus.EmAnalise ||
        op.status === CompraVendaStatus.AceiteOrigemRealizado ||
        op.status === CompraVendaStatus.AceiteDestinoRealizado)
  );

  const handleOpenAprovacaoEmLote = () => {
    setOpenAprovacaoEmLoteModal(true);
  };

  const handleCloseAprovacaoEmLote = () => {
    setOpenAprovacaoEmLoteModal(false);
  };

  const handleAprovacaoEmLoteSuccess = async () => {
    setSelectionModel([]);
    await getOperacoes();
  };

  const onFilterChange = useCallback((filterModel: GridFilterModel) => {
    if (!filterModel.items) {
      setComprasVendasFilter([]);
      setPage(0);
      return;
    }

    setLinkOperator(filterModel?.linkOperator?.toString()?.toUpperCase() ?? 'AND');

    const newComprasVendasFilter = new Array<ComprasVendasFilter>();
    filterModel.items.forEach((filter) => {
      const operatorValue =
        filter.operatorValue == 'startsWith'
          ? 'STARTS_WITH'
          : filter.operatorValue == 'endsWith'
          ? 'ENDS_WITH'
          : filter.operatorValue?.toUpperCase();

      let formattedValue = filter.value?.toString();
      if (formattedValue) {
        if (filter.columnField === 'DATA_DE_CRIACAO') {
          const parts = formattedValue.split('-');
          if (parts.length === 3) {
            formattedValue = `${parts[2]}/${parts[1]}/${parts[0]}`;
          }
        }

        const params = {
          columnType: filter.columnField.toUpperCase(),
          operationFilterType: operatorValue,
          value: formattedValue,
        } as ComprasVendasFilter;

        newComprasVendasFilter.push(params);
      }
    });

    setComprasVendasFilter(newComprasVendasFilter);
    setPage(0);
  }, []);

  const onSortChange = useCallback(
    (sortModel: GridSortModel) => {
      const newComprasVendasFilter = comprasVendasFilter.filter(
        (x) => x.orderByType != 'ASC' && x.orderByType != 'DESC'
      ) as Array<ComprasVendasFilter>;

      if (sortModel.length === 0 || sortModel == null) {
        setComprasVendasFilter([...newComprasVendasFilter]);
        setPage(0);
        return;
      }

      const filter = sortModel[0];
      const params = {
        columnType: filter.field.toUpperCase(),
        orderByType: filter.sort?.toUpperCase(),
      } as ComprasVendasFilter;

      newComprasVendasFilter.push(params);
      setComprasVendasFilter([...newComprasVendasFilter]);
      setPage(0);
    },
    [comprasVendasFilter]
  );

  const filterOperators = getGridStringOperators().filter(({ value }) =>
    ['equals', 'contains', 'startsWith', 'endsWith', 'is'].includes(value)
  );

  const filterOperatorsSelect = getGridSingleSelectOperators().filter(({ value }) => ['is', 'not'].includes(value));

  const filterOperatorsData = getGridDateOperators().filter(({ value }) =>
    ['is', 'not', 'after', 'onOrAfter', 'before', 'onOrBefore'].includes(value)
  );

  const statusValueOptions = [
    ...Object.keys(OperacaoCommonStatus).map((key) => ({
      value: (OperacaoCommonStatus as any)[key],
      label: operacaoStatusDescriptions[(OperacaoCommonStatus as any)[key]],
    })),
    ...Object.keys(CompraVendaStatus).map((key) => ({
      value: (CompraVendaStatus as any)[key],
      label: operacaoStatusDescriptions[(CompraVendaStatus as any)[key]],
    })),
  ];

  const getGridColumns = useCallback(
    () => [
      {
        field: 'STATUS',
        headerName: 'Status',
        type: 'singleSelect',
        valueOptions: statusValueOptions,
        filterOperators: filterOperatorsSelect,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 80,
        minWidth: 60,
        maxWidth: 100,
        valueGetter: (params: GridValueGetterParams) => (params.row as CompraVendaResumoResponse).status,
        renderCell: (params: GridCellParams) => {
          const item = params.row as CompraVendaResumoResponse;
          return (
            <Tooltip
              title={operacaoStatusDescriptions[item.status] ?? item.status}
              aria-label={operacaoStatusDescriptions[item.status]}
            >
              {getStatusIcon(item.status, classes)}
            </Tooltip>
          );
        },
      },
      {
        field: 'DATA_DE_CRIACAO',
        headerName: 'Data de criação',
        type: 'date',
        filterOperators: filterOperatorsData,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 180,
        minWidth: 150,
        valueGetter: (params: GridValueGetterParams) => new Date((params.row as CompraVendaResumoResponse).dataCriacao),
        renderCell: (params: GridCellParams) => {
          const item = params.row as CompraVendaResumoResponse;
          return <Typography variant={'body2'}>{formatDate(new Date(item.dataCriacao))}</Typography>;
        },
      },
      {
        field: 'CODIGO',
        headerName: 'Código',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 160,
        minWidth: 120,
        valueGetter: (params: GridValueGetterParams) => (params.row as CompraVendaResumoResponse).codigo,
        renderCell: (params: GridCellParams) => {
          const item = params.row as CompraVendaResumoResponse;
          return <Typography variant={'body2'}>{item.codigo}</Typography>;
        },
      },
      {
        field: 'RAZAO_SOCIAL_CUSTODIANTE_ORIGEM',
        headerName: 'Custodiante origem',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 220,
        minWidth: 180,
        valueGetter: (params: GridValueGetterParams) => (params.row as CompraVendaResumoResponse).custodianteOrigem,
        renderCell: (params: GridCellParams) => {
          const item = params.row as CompraVendaResumoResponse;
          return <Typography variant={'body2'}>{item.custodianteOrigem || '-'}</Typography>;
        },
      },
      {
        field: 'RAZAO_SOCIAL_CUSTODIANTE_DESTINO',
        headerName: 'Custodiante destino',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 220,
        minWidth: 180,
        valueGetter: (params: GridValueGetterParams) => (params.row as CompraVendaResumoResponse).custodianteDestino,
        renderCell: (params: GridCellParams) => {
          const item = params.row as CompraVendaResumoResponse;
          return <Typography variant={'body2'}>{item.custodianteDestino || '-'}</Typography>;
        },
      },
      {
        field: 'RAZAO_SOCIAL_INVESTIDOR_ORIGEM',
        headerName: 'Investidor origem',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 220,
        minWidth: 180,
        valueGetter: (params: GridValueGetterParams) => (params.row as CompraVendaResumoResponse).investidorOrigem,
        renderCell: (params: GridCellParams) => {
          const item = params.row as CompraVendaResumoResponse;
          return <Typography variant={'body2'}>{item.investidorOrigem}</Typography>;
        },
      },
      {
        field: 'RAZAO_SOCIAL_INVESTIDOR_DESTINO',
        headerName: 'Investidor destino',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 220,
        minWidth: 180,
        valueGetter: (params: GridValueGetterParams) => (params.row as CompraVendaResumoResponse).investidorDestino,
        renderCell: (params: GridCellParams) => {
          const item = params.row as CompraVendaResumoResponse;
          return <Typography variant={'body2'}>{item.investidorDestino}</Typography>;
        },
      },
      {
        field: 'RAZAO_SOCIAL_EMISSOR',
        headerName: 'Emissor',
        type: 'string',
        filterOperators,
        filterable: true,
        sortable: true,
        disableExport: false,
        width: 220,
        minWidth: 180,
        valueGetter: (params: GridValueGetterParams) => (params.row as CompraVendaResumoResponse).emissor,
        renderCell: (params: GridCellParams) => {
          const item = params.row as CompraVendaResumoResponse;
          return <Typography variant={'body2'}>{item.emissor}</Typography>;
        },
      },
      colunaDocumentoCompraVenda,
      ...colunasRelatorioMovimentacao,
      {
        field: 'actions',
        headerName: ' ',
        filterable: false,
        sortable: false,
        disableExport: true,
        width: 60,
        minWidth: 60,
        maxWidth: 60,
        renderCell: (params: GridCellParams) => {
          const item = params.row as CompraVendaResumoResponse;

          const isOriginador = item.participanteOrigemId
            ? item.participanteOrigemId === participanteId
            : item.custodianteOrigem === participanteNome;
          const isContraparte = !isOriginador && !isUsuarioOperacoes;

          const podeCancelar =
            isOriginador && item.status === OperacaoCommonStatus.EmAnalise && !item.isOperacaoPreviamenteRealizada;
          const podeAprovar =
            (isContraparte && item.status === OperacaoCommonStatus.EmAnalise && item.isCompra !== true) ||
            (isOriginador && item.status === OperacaoCommonStatus.EmAnalise && item.isCompra === true) ||
            (isUsuarioOperacoes && item.status === OperacaoCommonStatus.EmAnalise) ||
            (!isOriginador && item.status === CompraVendaStatus.AguardandoAprovacaoADMFiduciario) ||
            (!isOriginador && item.status === CompraVendaStatus.AceiteOrigemRealizado) ||
            (isOriginador && item.status === CompraVendaStatus.AceiteDestinoRealizado);

          const podeReprovar =
            (isContraparte && item.status === OperacaoCommonStatus.EmAnalise) ||
            (isUsuarioOperacoes && item.status === OperacaoCommonStatus.EmAnalise) ||
            (item.isOperacaoPreviamenteRealizada && item.status === OperacaoCommonStatus.EmAnalise) ||
            (item.isOperacaoPreviamenteRealizada &&
              item.status === CompraVendaStatus.AceiteOrigemRealizado && !isOriginador) ||
            (item.isOperacaoPreviamenteRealizada &&
              item.status === CompraVendaStatus.AceiteDestinoRealizado && isOriginador);

          const podeConfirmarLiquidacao =
            item.status === CompraVendaStatus.AguardandoLiquidacao &&
            !item.isOperacaoPreviamenteRealizada &&
            !contraparteNaoPodeLiquidarRetorno(item.tipo, isOriginador, isUsuarioOperacoes);

          return (
            <MoreOptionsMenu disabled={loadingDrawer}>
              <MenuItem onClick={() => history.push(`/operacoes/gerenciar/compras-vendas/${item.operacaoId}`)}>
                <Description fontSize="small" style={{ marginRight: '10px' }} />
                {'Ver Detalhes'}
              </MenuItem>
              {podeAprovar && (
                <MenuItem onClick={() => handleOpenAprovar(item)}>
                  <CheckCircle fontSize="small" style={{ marginRight: '10px' }} />
                  {'Aprovar'}
                </MenuItem>
              )}
              {podeReprovar && (
                <MenuItem onClick={() => handleOpenReprovar(item)}>
                  <Cancel fontSize="small" style={{ marginRight: '10px' }} />
                  {'Reprovar'}
                </MenuItem>
              )}
              {podeCancelar && (
                <MenuItem onClick={() => handleOpenCancelar(item)}>
                  <Cancel fontSize="small" style={{ marginRight: '10px' }} />
                  {'Cancelar'}
                </MenuItem>
              )}
              {podeConfirmarLiquidacao && (
                <MenuItem onClick={() => handleOpenConfirmarLiquidacao(item.operacaoId)}>
                  <CheckCircle fontSize="small" style={{ marginRight: '10px' }} />
                  {'Confirmar Liquidação'}
                </MenuItem>
              )}
              <MenuItem onClick={() => handleOpenGestaoComprovantes(item)}>
                <AttachFile fontSize="small" style={{ marginRight: '10px' }} />
                {'Gestão de Comprovantes'}
              </MenuItem>
            </MoreOptionsMenu>
          );
        },
      },
    ],
    [
      filterOperators,
      filterOperatorsSelect,
      filterOperatorsData,
      statusValueOptions,
      history,
      loadingDrawer,
      handleOpenAprovar,
      handleOpenConfirmarLiquidacao,
    ]
  );

  useEffect(() => {
    getOperacoes();
  }, [getOperacoes]);

  return (
    <div>
      {showStatusToast && statusToastByAction(drawerAction)}
      <MdSpace />

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box style={{ flex: 1, minWidth: 0 }}>
          <LegendaStatus exibirStatusOpr exibirStatusAdmFiduciario />
        </Box>
        {selectionModel.length > 0 && operacoesSelecionadasEmAnalise.length > 0 && (
          <Button
            variant="contained"
            color="primary"
            onClick={handleOpenAprovacaoEmLote}
            style={{ whiteSpace: 'nowrap', marginLeft: 16 }}
          >
            {`Aprovar selecionadas (${operacoesSelecionadasEmAnalise.length})`}
          </Button>
        )}
      </Box>
      <MdSpace />
      <DataGridPro
        autoHeight
        localeText={localizedTextsMap}
        componentsProps={{
          ...componentsPropsValues,
          toolbar: {
            carregarTodasAsMovimentacoes,
            movimentacoesDaPaginaAtual: operacoes,
            nomeArquivo: 'movimentacoes-compra-venda',
          },
        }}
        getRowId={(row) => (row as CompraVendaResumoResponse).operacaoId}
        rows={operacoes}
        columns={getGridColumns()}
        pinnedColumns={{ right: ['actions'] }}
        components={{
          Toolbar: ToolbarExportacaoMovimentacoes,
        }}
        checkboxSelection
        disableSelectionOnClick
        isRowSelectable={(params) => {
          const item = params.row as CompraVendaResumoResponse;
          const isOriginador = item.participanteOrigemId
            ? item.participanteOrigemId === participanteId
            : item.custodianteOrigem === participanteNome;

          if (item.status === OperacaoCommonStatus.EmAnalise) return true;
          if (item.status === CompraVendaStatus.AguardandoAprovacaoADMFiduciario) return true;
          if (item.status === CompraVendaStatus.AceiteOrigemRealizado && !isOriginador) return true;
          if (item.status === CompraVendaStatus.AceiteDestinoRealizado && isOriginador) return true;
          return false;
        }}
        selectionModel={selectionModel}
        onSelectionModelChange={(newModel) => setSelectionModel(newModel)}
        rowsPerPageOptions={rowsPerPageOptionsValues}
        page={page}
        rowCount={pageCount}
        pagination
        paginationMode="server"
        onPageChange={(newPage) => setPage(newPage)}
        onPageSizeChange={(newPageSize) => {
          setPageSize(newPageSize);
          setPage(0);
        }}
        pageSize={pageSize}
        filterMode="server"
        onFilterModelChange={onFilterChange}
        sortingMode="server"
        onSortModelChange={onSortChange}
      />

      {openAprovarRejeitarDrawer && operacao && (
        <GenericDrawer
          opened={openAprovarRejeitarDrawer}
          disableButtons={submitting}
          onClose={handleCloseDrawer}
          title={`${drawerAction === DrawerAction.ACEITAR ? 'Aprovar' : drawerAction === DrawerAction.CANCELAR ? 'Cancelar' : 'Rejeitar'} Movimentação`}
          textContents={[
            {
              text: `Tem certeza que deseja ${
                drawerAction === DrawerAction.ACEITAR ? 'aprovar' : drawerAction === DrawerAction.CANCELAR ? 'cancelar' : 'rejeitar'
              } a movimentação por Compra e Venda referente ao instrumento financeiro ${
                operacao.instrumentoFinanceiro.codigo
              }?`,
            },
            { subtitle: 'Quantidade:', text: operacao.compraVenda?.quantidade?.toString() ?? '0' },
            {
              subtitle: 'Preço Unitário:',
              text: formatEightDecimalsCurrency(operacao.compraVenda?.precoUnitario ?? 0),
            },
            {
              subtitle: 'Valor Total:',
              text: formatInstrumentoCurrency(
                (operacao.compraVenda.quantidade ?? 0) * (operacao.compraVenda.precoUnitario ?? 0),
                operacao.instrumentoFinanceiro?.tipoInstrumentoFinanceiro
              ),
            },
            {
              subtitle: 'CPF/CNPJ do Comprador:',
              text: safeGet(operacao, 'compraVenda.investidorDestino.cpfCnpj', '')
                ? addCpfCnpjMask(safeGet(operacao, 'compraVenda.investidorDestino.cpfCnpj', ''))
                : 'Não informado',
            },
            {
              subtitle: 'Conta Depósito do Comprador:',
              text: safeGet(operacao, 'compraVenda.investidorDestino.contaDeposito', 'Não informada'),
            },
            { subtitle: 'Liquidação Direta:', text: operacao.compraVenda.liquidacaoDireta ? 'Sim' : 'Não' },
          ]}
          buttons={[
            {
              buttonText: drawerAction === DrawerAction.ACEITAR ? 'Aprovar' : drawerAction === DrawerAction.CANCELAR ? 'Cancelar Operação' : 'Reprovar',
              buttonAction: drawerAction === DrawerAction.ACEITAR ? () => handleAceitar() : drawerAction === DrawerAction.CANCELAR ? () => handleCancelar() : () => handleRejeitar(),
              buttonColor: drawerAction === DrawerAction.ACEITAR ? 'primary' : 'error',
              buttonDisable: (drawerAction === DrawerAction.REJEITAR || drawerAction === DrawerAction.CANCELAR) && justificativa.trim() === '',
            },
            {
              buttonText: 'Voltar',
              buttonAction: () => handleCloseDrawer(),
            },
          ]}
        >
          {instrumentoInadimplido && (
            <>
              <Alert severity="error">
                {'Este Instrumento Financeiro está Inadimplido, ao continuar com a Movimentação você declara ciência.'}
              </Alert>
              <SmSpace />
            </>
          )}
          {(drawerAction === DrawerAction.REJEITAR || drawerAction === DrawerAction.CANCELAR) && (
            <TextField
              label="Justificativa *"
              placeholder="Informe a justificativa"
              multiline
              minRows={3}
              fullWidth
              variant="outlined"
              value={justificativa}
              onChange={(e) => setJustificativa(e.target.value)}
              error={justificativa.trim() === ''}
              helperText={justificativa.trim() === '' ? 'Justificativa é obrigatória' : ''}
              style={{ marginBottom: 16 }}
            />
          )}
        </GenericDrawer>
      )}

      {openAprovarRejeitarADMFiduciarioDrawer && operacao && (
        <GenericDrawer
          opened={openAprovarRejeitarADMFiduciarioDrawer}
          disableButtons={submitting}
          onClose={handleCloseDrawer}
          title={`${drawerAction === DrawerAction.APROVACAO_ADM_FIDUCIARIO ? 'Aprovar' : 'Rejeitar'} Movimentação`}
          textContents={[
            {
              text: `Tem certeza que deseja ${
                drawerAction === DrawerAction.APROVACAO_ADM_FIDUCIARIO ? 'aprovar' : 'rejeitar'
              } a movimentação por Compra e Venda referente ao instrumento financeiro ${
                operacao.instrumentoFinanceiro.codigo
              }?`,
            },
            { subtitle: 'Quantidade:', text: operacao.compraVenda?.quantidade?.toString() ?? '0' },
            {
              subtitle: 'Preço Unitário:',
              text: formatEightDecimalsCurrency(operacao.compraVenda?.precoUnitario ?? 0),
            },
            {
              subtitle: 'Valor Total:',
              text: formatInstrumentoCurrency(
                (operacao.compraVenda.quantidade ?? 0) * (operacao.compraVenda.precoUnitario ?? 0),
                operacao.instrumentoFinanceiro?.tipoInstrumentoFinanceiro
              ),
            },
            {
              subtitle: 'CPF/CNPJ do Comprador:',
              text: safeGet(operacao, 'compraVenda.investidorDestino.cpfCnpj', '')
                ? addCpfCnpjMask(safeGet(operacao, 'compraVenda.investidorDestino.cpfCnpj', ''))
                : 'Não informado',
            },
            {
              subtitle: 'Conta Depósito do Comprador:',
              text: safeGet(operacao, 'compraVenda.investidorDestino.contaDeposito', 'Não informada'),
            },
            { subtitle: 'Liquidação Direta:', text: operacao.compraVenda.liquidacaoDireta ? 'Sim' : 'Não' },
          ]}
          buttons={[
            {
              buttonText: drawerAction === DrawerAction.APROVACAO_ADM_FIDUCIARIO ? 'Aceitar' : 'Rejeitar',
              buttonAction:
                drawerAction === DrawerAction.APROVACAO_ADM_FIDUCIARIO ? () => handleAceitar() : () => handleRejeitar(),
              buttonColor: drawerAction === DrawerAction.APROVACAO_ADM_FIDUCIARIO ? 'primary' : 'error',
            },
            {
              buttonText: 'Cancelar',
              buttonAction: () => handleCloseDrawer(),
            },
          ]}
        >
          {instrumentoInadimplido && (
            <>
              <Alert severity="error">
                {'Este Instrumento Financeiro está Inadimplido, ao continuar com a Movimentação você declara ciência.'}
              </Alert>
              <SmSpace />
            </>
          )}
        </GenericDrawer>
      )}

      {openConfirmarLiquidacaoDrawer && operacao && (
        <GenericDrawer
          opened={openConfirmarLiquidacaoDrawer}
          onClose={handleCloseDrawer}
          textContents={[
            {
              text: `Tem certeza que deseja confirmar a liquidação da movimentação por Compra e Venda referente ao instrumento financeiro ${operacao.instrumentoFinanceiro.codigo}?`,
            },
            { subtitle: 'Quantidade:', text: operacao.compraVenda?.quantidade?.toString() ?? '' },
            {
              subtitle: 'Preço Unitário:',
              text: formatEightDecimalsCurrency(operacao.compraVenda?.precoUnitario ?? 0),
            },
            {
              subtitle: 'Valor Total:',
              text: formatInstrumentoCurrency(
                (operacao.compraVenda.quantidade ?? 0) * (operacao.compraVenda.precoUnitario ?? 0),
                operacao.instrumentoFinanceiro?.tipoInstrumentoFinanceiro
              ),
            },
            {
              subtitle: 'CPF/CNPJ do Comprador:',
              text: safeGet(operacao, 'compraVenda.investidorDestino.cpfCnpj', '')
                ? addCpfCnpjMask(safeGet(operacao, 'compraVenda.investidorDestino.cpfCnpj', ''))
                : 'Não informado',
            },
            {
              subtitle: 'Conta Depósito do Comprador:',
              text: safeGet(operacao, 'compraVenda.investidorDestino.contaDeposito', 'Não informada'),
            },
            { subtitle: 'Liquidação Direta:', text: operacao.compraVenda.liquidacaoDireta ? 'Sim' : 'Não' },
          ]}
          title={'Confirmar Liquidação'}
          buttons={[
            {
              buttonText: 'Confirmar Liquidação',
              buttonAction: () => handleConfirmarLiquidacao(),
              buttonColor: 'primary',
            },
            {
              buttonText: 'Cancelar',
              buttonAction: () => handleCloseDrawer(),
            },
          ]}
        />
      )}

      <GestaoComprovantesDrawer
        operacaoId={comprovantesOperacaoId}
        codigoInstrumento={comprovantesCodigoIF}
        comprovantes={compraVendaDocumentos}
        opened={openGestaoComprovantesDrawer}
        onClose={handleCloseGestaoComprovantes}
        onUploadSuccess={handleGestaoComprovantesUploadSuccess}
      />

      <AprovacaoEmLoteModal
        open={openAprovacaoEmLoteModal}
        operacoes={operacoesSelecionadasEmAnalise}
        onClose={handleCloseAprovacaoEmLote}
        onSuccess={handleAprovacaoEmLoteSuccess}
      />
    </div>
  );
};

export default ComprasVendas;
